import React, { useEffect, useState } from "react";
import gsap from "gsap";
import { Link } from "react-router-dom";
import {
  subMenuOpen,
  subMenuClose,
} from "../../../utils/Actions/subMenuOpenClose";

import NavMenuItem from "./NavMenuItem";
import { useGuildPath } from "../../../utils/Guild/useGuild";

export default function NavMenu({
  navData,
  activeSection,
  setActiveSection,
  navRefs,
  indicatorRef,
  submenuTimelines,
  programmaticScroll,
}) {
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [sortedNavData, setSortedNavData] = useState([]);
  const guildPath = useGuildPath();

  useEffect(() => {
    if (!navData) {
      setSortedNavData([]);
      return;
    }

    const normalizeTitle = (title = "") =>
      title
        .trim()
        .replace(/^the\s+/i, "")
        .toLowerCase();

    const updatedNavData = navData.map((item) => ({
      ...item,
      shows: Array.isArray(item.shows)
        ? [...item.shows].sort((a, b) =>
            normalizeTitle(a.title).localeCompare(normalizeTitle(b.title)),
          )
        : [],
    }));

    setSortedNavData(updatedNavData);
  }, [navData]);

  // Initialize submenu heights
  useEffect(() => {
    Object.values(navRefs.current).forEach((navItem) => {
      if (!navItem) return;
      const submenu = navItem.querySelector(".submenu");
      if (submenu) {
        submenu.dataset.height = submenu.scrollHeight;
        gsap.set(submenu, { height: 0, opacity: 0 });
      }
    });
  }, [sortedNavData]);

  useEffect(() => {
    if (!sortedNavData.length) return;

    const firstItem = sortedNavData[0];
    const navItem = navRefs.current[firstItem.slug];
    if (!navItem) return;

    const menu = navItem.querySelector(".submenu");
    if (!menu) return;

    const items = menu.querySelectorAll(".submenu-item");

    subMenuOpen(menu, items, submenuTimelines);
    setOpenSubmenu("nav-" + firstItem.slug);
  }, [sortedNavData, navRefs, submenuTimelines]);

  const getElementY = (el) => {
    const rect = el.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return rect.top + scrollTop; // absolute Y position in the document
  };

  const scrollToSection = (slug) => {
    const el = document.getElementById(slug);
    if (!el) return;

    programmaticScroll.current = true;
    const targetY = getElementY(el);
    // console.log("scrollToSection", el, targetY);
    gsap.to(window, {
      scrollTo: { y: targetY, autoKill: false },
      duration: 0.25,
      ease: "power3.out",
      onComplete: () => {
        programmaticScroll.current = false;
        setActiveSection(slug);
      },
    });
  };

  const handleNavItemControlClick = (e) => {
    const navItem = e.target.closest(".nav-item");
    if (!navItem) return;

    const navItemInner = e.target.closest(".nav-item-inner");
    if (!navItemInner) return;

    const menu = navItemInner.nextElementSibling;
    if (!menu?.classList.contains("submenu")) return;

    const items = menu.querySelectorAll(".submenu-item");
    if (navItem.classList.contains("nav-open")) {
      subMenuClose(menu, items, submenuTimelines);
    } else {
      subMenuOpen(menu, items, submenuTimelines);
    }
    setOpenSubmenu((prev) => (prev === navItem.id ? null : navItem.id));
  };

  return (
    <nav className="nav" aria-label="Main site navigation">
      <div className="nav-indicator" ref={indicatorRef}></div>
      <ul className="nav-ul">
        {navData &&
          sortedNavData &&
          sortedNavData.map((item) => (
            <li
              key={item.slug}
              id={"nav-" + item.slug}
              className={
                "nav-item " + (activeSection === item.slug ? "active" : "")
              }
              ref={(el) => (navRefs.current[item.slug] = el)}
            >
              <div className="nav-item-inner">
                <button
                  type="button"
                  className="nav-item-label"
                  onClick={() => {
                    scrollToSection(item.slug);
                    // setActiveSection(item.slug);
                  }}
                  aria-current={
                    activeSection === item.slug ? "true" : undefined
                  }
                >
                  {item.title}
                </button>

                <button
                  type="button"
                  className="nav-item-control"
                  onClick={handleNavItemControlClick}
                  aria-expanded={
                    openSubmenu === "nav-" + item.slug ? "true" : "false"
                  }
                  aria-controls={`submenu-${item.slug}`}
                >
                  <img
                    src="/img/ico/chevron-down.png"
                    alt={`Toggle submenu for ${item.title}`}
                    className="nav-item-icon"
                  />
                  <span className="sr-only">
                    Toggle submenu for {item.title}
                  </span>
                </button>

                <div className="nav-item-seeall">
                  <Link
                    to={guildPath(`/${item.slug}`)}
                    aria-label={`See all ${item.title} shows`}
                    // style={{ color: item?.theme?.colors?.light?.hex }}
                  >
                    View All
                  </Link>
                </div>
              </div>

              <div
                className="submenu"
                id={`submenu-${item.slug}`}
                data-key={item.slug}
              >
                <div className="submenu-items">
                  {item.shows &&
                    item.shows.map((submenuItem) => (
                      <NavMenuItem
                        key={submenuItem.slug}
                        parent={item}
                        submenuItem={submenuItem}
                        isMobile={false}
                      />
                    ))}
                </div>
                <div className="submenu-bg" aria-hidden="true" />
              </div>
            </li>
          ))}
      </ul>
    </nav>
  );
}
